import React, { useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { X, Loader2, AlertTriangle, Send, Mail } from 'lucide-react';

/**
 * BKDecisionEmailModal — Burger King candidate-facing Selection notification.
 *
 * DELIBERATELY SEPARATE FROM `DecisionEmailModal.js` (Go Colors), which it shares no code
 * with. The two look alike today but answer to different clients: Go Colors covers
 * Selected/Rejected/Dropped and CCs a store's ARM; this covers Selected only and CCs the HRBP
 * for the job post's region. Sharing one component would mean either client's copy, recipient
 * rules or extra decisions leaking into the other's email. They are allowed to drift.
 *
 * Opens after a recruiter moves a candidate to Selected (when the `bk_decision_emails` feature
 * is on). Fetches a server-resolved preview (From = recruiter@talentcrux.ai, To = candidate,
 * CC = the region's HRBP), lets HR edit the subject/body/CC, then sends. The candidate is only
 * moved to Selected once the email actually goes out — see `onSent`.
 */
const BKDecisionEmailModal = ({ screeningId, candidateId, candidateName, onClose, onSent }) => {
  const [loading, setLoading] = useState(true);
  const [blockedReason, setBlockedReason] = useState('');
  const [preview, setPreview] = useState(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [cc, setCc] = useState(''); // region HRBP, prefilled and editable
  const [sending, setSending] = useState(false);

  const base = `/api/bk-decision-emails/${screeningId}/candidates/${candidateId}/decision-email`;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setBlockedReason('');
      try {
        const res = await axios.get(`${base}/preview`, { params: { decision: 'selected' } });
        if (cancelled) return;
        if (res.data?.blocked) {
          setBlockedReason(res.data.reason || 'This email cannot be sent.');
        } else {
          setPreview(res.data);
          setSubject(res.data.subject || '');
          setBody(res.data.body || '');
          setCc((res.data.cc || []).join(', '));
        }
      } catch (err) {
        if (cancelled) return;
        const status = err.response?.status;
        if (status === 422 && err.response?.data?.reason) {
          setBlockedReason(err.response.data.reason);
        } else if (status === 403) {
          setBlockedReason('This feature is not enabled for your organisation.');
        } else {
          setBlockedReason(err.response?.data?.message || 'Failed to load the email preview.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [base]);

  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const ccValid = cc.trim() === '' || cc.split(',').every((e) => EMAIL_RE.test(e.trim()));

  const handleSend = async () => {
    if (!subject.trim()) {
      toast.error('Subject cannot be empty.');
      return;
    }
    if (!ccValid) {
      toast.error('Enter valid CC email addresses.');
      return;
    }
    setSending(true);
    try {
      await axios.post(`${base}/send`, {
        decision: 'selected',
        subject,
        body,
        cc: cc.trim(),
      });
      toast.success('Shortlisted email sent.');
      if (onSent) await onSent();
      onClose();
    } catch (err) {
      const status = err.response?.status;
      if (status === 422 && err.response?.data?.reason) {
        setBlockedReason(err.response.data.reason);
      } else {
        toast.error(err.response?.data?.message || 'Failed to send the email.');
      }
    } finally {
      setSending(false);
    }
  };

  const recipientRow = (label, value) => (
    <div className="flex gap-2 text-xs">
      <span className="w-12 shrink-0 font-semibold text-gray-400 uppercase tracking-wide">
        {label}
      </span>
      <span className="text-gray-800 break-all">{value || '—'}</span>
    </div>
  );

  return (
    <div className="fixed inset-0 z-[10000] flex items-start justify-center overflow-y-auto bg-black/40 backdrop-blur-[2px] p-4 sm:items-center">
      <div className="flex w-full max-w-3xl max-h-[calc(100dvh-2rem)] flex-col overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-2xl">
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between border-b border-gray-100 bg-white px-5 py-4">
          <h2 className="flex min-w-0 items-center gap-2 text-base font-semibold text-gray-900">
            <Mail className="w-5 h-5 text-[#007BFF]" />
            <span className="truncate">Send Shortlisted Email</span>
            {candidateName ? (
              <span className="text-gray-400 font-normal text-sm">· {candidateName}</span>
            ) : null}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-5">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12 gap-3">
              <Loader2 className="w-8 h-8 animate-spin text-[#007BFF]" />
              <p className="text-sm text-gray-500">Preparing the email…</p>
            </div>
          ) : blockedReason ? (
            <div className="flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
              <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5 text-amber-500" />
              <div>
                <p className="font-semibold">This email can’t be sent yet</p>
                <p className="mt-0.5">{blockedReason}</p>
              </div>
            </div>
          ) : (
            <>
              {(preview?.warnings || []).map((w, i) => (
                <div
                  key={i}
                  className="rounded-lg border border-yellow-200 bg-yellow-50 p-2.5 text-xs text-yellow-800"
                >
                  {w}
                </div>
              ))}

              {/* Resolved recipients (From/To read-only; CC is the region HRBP, editable) */}
              <div className="space-y-1.5 rounded-xl border border-gray-200 bg-gray-50/60 p-3">
                {recipientRow(
                  'From',
                  `${preview?.fromName ? `${preview.fromName} · ` : ''}${preview?.from}`
                )}
                {recipientRow('To', preview?.to)}
                <div className="flex items-center gap-2 text-xs">
                  <span className="w-12 shrink-0 font-semibold text-gray-400 uppercase tracking-wide">
                    CC
                  </span>
                  <input
                    type="text"
                    value={cc}
                    onChange={(e) => setCc(e.target.value)}
                    placeholder="HRBP email (optional)"
                    className={`flex-1 rounded-lg border px-2.5 py-1.5 text-xs text-gray-900 outline-none focus:ring-1 ${
                      cc && !ccValid
                        ? 'border-red-300 focus:border-red-400 focus:ring-red-400'
                        : 'border-gray-200 focus:border-[#007BFF] focus:ring-[#007BFF]'
                    }`}
                  />
                </div>
                {/* Names the region the CC was resolved from, so HR can spot a wrong Team
                    before sending rather than after. */}
                {preview?.team ? (
                  <p className="pl-14 text-[11px] text-gray-400">
                    HRBP resolved from region <span className="font-medium">{preview.team}</span>
                  </p>
                ) : null}
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Subject</label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="w-full rounded-xl border border-gray-200 px-3.5 py-2.5 text-sm text-gray-900 outline-none shadow-sm focus:border-[#007BFF] focus:ring-1 focus:ring-[#007BFF]"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Message</label>
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={9}
                  className="w-full resize-y rounded-xl border border-gray-200 px-3.5 py-2.5 text-sm leading-relaxed text-gray-900 outline-none shadow-sm focus:border-[#007BFF] focus:ring-1 focus:ring-[#007BFF]"
                />
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex shrink-0 items-center justify-end gap-3 border-t border-gray-100 bg-white px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            disabled={sending}
            className="rounded-xl px-5 py-2.5 text-sm font-semibold text-gray-600 hover:bg-gray-100 transition disabled:opacity-50"
          >
            {blockedReason ? 'Close' : 'Cancel'}
          </button>
          {!loading && !blockedReason && (
            <button
              type="button"
              onClick={handleSend}
              disabled={sending || !ccValid}
              className="inline-flex items-center gap-2 rounded-xl bg-[#007BFF] hover:bg-blue-600 px-6 py-2.5 text-sm font-semibold text-white shadow-md hover:shadow-lg transition disabled:opacity-50"
            >
              {sending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
              {sending ? 'Sending…' : 'Send Email'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BKDecisionEmailModal;
